import * as Sentry from "@sentry/react";
import { useIntl } from "react-intl";

import type { HotelData } from "@/features/hotels/types/types";

interface HotelMapProps {
  hotel: HotelData;
}

export const HotelMap = ({ hotel }: HotelMapProps) => {
  const intl = useIntl();

  if (!hotel.geolocation && !hotel.address) return null;

  const { latitude, longitude } = hotel.geolocation ?? {};
  const query =
    latitude != null && longitude != null
      ? `${latitude},${longitude}`
      : [hotel.name, hotel.address].filter(Boolean).join(", ");
  const apiKey = import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY;
  const mode = latitude != null && longitude != null ? "place" : "search";
  const googleMapsUrl = `https://www.google.com/maps/embed/v1/${mode}?key=${apiKey}&q=${encodeURIComponent(query)}&zoom=13`;

  return (
    <div className="overflow-hidden rounded-2xl">
      <iframe
        src={googleMapsUrl}
        title={intl.formatMessage({
          defaultMessage: "Hotel location map",
          description: "Title attribute for the embedded map iframe",
        })}
        // must have at least 200px height to display map controls
        className="aspect-video min-h-50 w-full border-0"
        loading="lazy"
        allowFullScreen
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer-when-downgrade"
        onError={() => {
          Sentry.captureException(
            new Error("Google Maps embed iframe failed to load"),
            {
              extra: {
                mapMode: mode,
                query,
              },
            },
          );
        }}
      />
    </div>
  );
};
